// Nocturnal Racing — global brand script. Kept tiny: anything section-specific
// lives in its own nr-*.js file loaded only by the section that needs it.
import { getScrollTop, getIntersectionRoot } from '@theme/scroll-container';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Night clock: shows the visitor's own local time. Real data, not a countdown.
const clockFormat = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

function tickClocks() {
  const now = new Date();
  const hour = now.getHours();
  // "Night shift" = 7pm to 6am local
  const onShift = hour >= 19 || hour < 6;
  for (const clock of document.querySelectorAll('[data-nr-clock]')) {
    const time = clock.querySelector('[data-nr-clock-time]');
    const status = clock.querySelector('[data-nr-clock-status]');
    if (time) time.textContent = clockFormat.format(now);
    if (status) status.textContent = onShift ? clock.dataset.on : clock.dataset.off;
    clock.toggleAttribute('data-on-shift', onShift);
  }
}

tickClocks();
setInterval(tickClocks, 15000);

// Ticker speed follows scroll velocity (Web Animations API, compositor-only).
// Horizon scrolls the document on mobile and .page-wrapper on desktop, so listen to both.
let lastY = getScrollTop();
let lastT = performance.now();
let boost = 0;
let raf = 0;

function applyBoost() {
  boost *= 0.92;
  for (const track of document.querySelectorAll('[data-nr-ticker-track]')) {
    if (track.closest('.is-paused')) continue;
    const anim = track.getAnimations()[0];
    if (anim) anim.playbackRate = 1 + Math.min(boost, 6);
  }
  raf = boost > 0.02 ? requestAnimationFrame(applyBoost) : 0;
}

function onScroll() {
  if (reduceMotion.matches) return;
  const y = getScrollTop();
  const now = performance.now();
  const dy = Math.abs(y - lastY);
  const dt = Math.max(now - lastT, 16);
  lastY = y;
  lastT = now;
  boost = Math.max(boost, (dy / dt) * 2.2);
  if (!raf) raf = requestAnimationFrame(applyBoost);
}

window.addEventListener('scroll', onScroll, { passive: true });
document.querySelector('.page-wrapper')?.addEventListener('scroll', onScroll, { passive: true });

// Pause buttons on tickers and looping media (WCAG 2.2.2). One delegated handler.
document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-nr-pause]') : null;
  if (!btn) return;
  const host = btn.closest('[data-nr-pausable]');
  if (!host) return;
  const paused = !host.classList.contains('is-paused');
  host.classList.toggle('is-paused', paused);
  btn.textContent = paused ? btn.dataset.playLabel : btn.dataset.pauseLabel;
  const video = host.querySelector('video');
  if (video) {
    video.dataset.userPaused = paused ? '1' : '';
    if (paused) video.pause();
    else video.play().catch(() => {});
  }
});

// Silent loops in brand sections: load and play only while on screen, never with reduced
// motion. Posters are deferred too, so slots far down the page cost nothing at load.
const videoObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const { target: video, isIntersecting } of entries) {
            if (isIntersecting && video.dataset.poster && !video.poster) video.poster = video.dataset.poster;
            if (!isIntersecting || reduceMotion.matches || video.dataset.userPaused) {
              video.pause();
              continue;
            }
            if (video.dataset.src && !video.src) video.src = video.dataset.src;
            video.play().catch(() => {});
          }
        },
        { root: getIntersectionRoot(), rootMargin: '200px 0px' }
      )
    : null;

function watchVideos(root = document) {
  root.querySelectorAll('video[data-nr-autoplay]').forEach((video) => videoObserver?.observe(video));
}

// Start after load so an above-the-fold loop never competes with the LCP image.
if (document.readyState === 'complete') watchVideos();
else window.addEventListener('load', () => watchVideos(), { once: true });

// The theme editor re-renders sections in place.
document.addEventListener('shopify:section:load', (event) => {
  tickClocks();
  watchVideos(event.target);
});

// Signup forms: keep the "you're on the list" note only on the form that was submitted.
function scopeSignupSuccess() {
  const posted = document.querySelectorAll('form.nr-signup [data-nr-signup-done]');
  const target = decodeURIComponent(window.location.hash.slice(1));
  if (!posted.length || !target) return;
  for (const done of posted) {
    const form = done.closest('form');
    if (form.id === target) continue;
    done.remove();
    form.querySelector('.nr-signup__row')?.removeAttribute('hidden');
  }
}

scopeSignupSuccess();

// "Send to your crew": native share sheet on phones, copy link elsewhere. Delegated so it
// also works in re-rendered sections and drawers.
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-nr-share]') : null;
  if (!btn) return;
  const { url, text, title } = btn.dataset;
  const label = btn.querySelector('[data-nr-share-label]');
  try {
    if (navigator.share) {
      await navigator.share({ title, text, url });
      return;
    }
    await navigator.clipboard.writeText(`${text} ${url}`);
    if (label) {
      const idle = (label.dataset.idle ??= label.textContent);
      label.textContent = btn.dataset.copied;
      setTimeout(() => {
        label.textContent = idle;
      }, 2000);
    }
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('share failed', error);
  }
});
